import { useCallback, useEffect, useRef, useState } from 'react';
import { Mic, MicOff, X, Loader2, Check, Minimize2, Maximize2 } from 'lucide-react';
import { useMe } from '../../hooks/useMe';
import { useCreateVoiceFeedback } from '../../hooks/useVoiceFeedback';
import { Button, IconButton } from '../common';

const TIMER_LIMIT_SECONDS = 7 * 60;

type WidgetState = 'closed' | 'expanded' | 'mini';

// Pragmatic — Web Speech API typings are inconsistent across libs.
type SpeechRecognitionLike = any;

function isDevHostname(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host.startsWith('dev.');
}

function formatTimer(elapsedSeconds: number): string {
  const remaining = TIMER_LIMIT_SECONDS - elapsedSeconds;
  if (remaining >= 0) {
    const mm = Math.floor(remaining / 60);
    const ss = remaining % 60;
    return `${mm}:${ss.toString().padStart(2, '0')}`;
  }
  const over = Math.abs(remaining);
  const mm = Math.floor(over / 60);
  const ss = over % 60;
  return `+${mm}:${ss.toString().padStart(2, '0')}`;
}

function getLastNonEmptyLine(text: string): string {
  if (!text) return '';
  const lines = text.split(/\r?\n/);
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i].trim();
    if (line.length > 0) return line;
  }
  return '';
}

// Fold a just-finished session's final text into the running committed base
// without the mobile "staircase". On Android Chrome continuous=true is ignored:
// the engine ends the session after each speech segment and, on restart,
// re-recognizes the SAME ongoing speech, so sessionFinal arrives as a growing
// version of text already committed. Plain concatenation then stacks
// overlapping prefixes ("I" → "I I am" → "I I am I am testing"). We strip the
// longest WORD-level overlap between the suffix of `committed` and the prefix
// of `addition` before appending the remainder. Word-level (not char-level)
// avoids partial-word glitches; comparison is case-insensitive but the kept
// text preserves its original casing/spacing.
function mergeWithOverlap(committed: string, addition: string): string {
  const trimmedAddition = addition.trim();
  if (!committed.trim()) return trimmedAddition;
  if (!trimmedAddition) return committed;

  const committedWords = committed.trim().split(/\s+/);
  const additionWords = trimmedAddition.split(/\s+/);

  const norm = (w: string) => w.toLowerCase();
  // Largest k where the last k words of committed equal the first k of addition.
  const maxOverlap = Math.min(committedWords.length, additionWords.length);
  let overlap = 0;
  for (let k = maxOverlap; k > 0; k--) {
    const suffix = committedWords.slice(committedWords.length - k);
    const prefix = additionWords.slice(0, k);
    let match = true;
    for (let i = 0; i < k; i++) {
      if (norm(suffix[i]) !== norm(prefix[i])) {
        match = false;
        break;
      }
    }
    if (match) {
      overlap = k;
      break;
    }
  }

  // The entire addition is already the suffix of committed — nothing new.
  if (overlap === additionWords.length) {
    return committed.trim().replace(/\s+/g, ' ');
  }

  const remainder = additionWords.slice(overlap).join(' ');
  const merged = remainder ? committed.trim() + ' ' + remainder : committed.trim();
  return merged.replace(/\s+/g, ' ').trim();
}

function getSpeechRecognitionCtor(): any {
  if (typeof window === 'undefined') return undefined;
  return (
    (window as any).SpeechRecognition ||
    (window as any).webkitSpeechRecognition
  );
}

export default function FloatingVoiceFeedback() {
  const { isAdmin, isLoading } = useMe();
  const [showOnDev, setShowOnDev] = useState(false);

  useEffect(() => {
    setShowOnDev(isDevHostname());
  }, []);

  const [widgetState, setWidgetState] = useState<WidgetState>('closed');
  const [transcript, setTranscript] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [isRecording, setIsRecording] = useState(false);
  const [interimText, setInterimText] = useState('');
  const [micError, setMicError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const wantRecordingRef = useRef<boolean>(false);
  // Text committed in PRIOR recognition sessions (sessions are separated by the
  // onend → rec.start() restart boundary). The CURRENT session's transcript is
  // recomputed idempotently from event.results[0..n] each onresult; folding it
  // into this ref on restart keeps text from earlier sessions.
  const committedTranscriptRef = useRef('');
  // Latest final string for the current session, mirrored from onresult so onend
  // can fold it into committedTranscriptRef before restarting.
  const sessionFinalRef = useRef('');
  const createVoiceFeedback = useCreateVoiceFeedback();

  const isOpen = widgetState !== 'closed';
  const speechSupported = typeof window !== 'undefined' && !!getSpeechRecognitionCtor();

  // Timer ticks whenever the widget is "open" (expanded OR mini).
  useEffect(() => {
    if (!isOpen) return;
    const interval = setInterval(() => {
      setElapsedSeconds((s) => s + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [isOpen]);

  // Focus textarea when entering expanded state.
  useEffect(() => {
    if (widgetState === 'expanded') {
      textareaRef.current?.focus();
    }
  }, [widgetState]);

  const stopRecognition = useCallback(() => {
    wantRecordingRef.current = false;
    const rec = recognitionRef.current;
    if (rec) {
      try {
        rec.onresult = null;
        rec.onerror = null;
        rec.onend = null;
        rec.stop();
      } catch {
        // ignore
      }
      recognitionRef.current = null;
    }
    setIsRecording(false);
    setInterimText('');
  }, []);

  const startRecognition = useCallback(() => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) return;

    // Tear down any prior instance first.
    if (recognitionRef.current) {
      try {
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.stop();
      } catch {
        // ignore
      }
      recognitionRef.current = null;
    }

    const rec: SpeechRecognitionLike = new Ctor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';

    rec.onresult = (event: any) => {
      // Rebuild the CURRENT session's transcript from results[0..n] every event.
      // Mobile Chrome emits each result as a cumulative FINAL snapshot of the
      // whole phrase so far ("1" / "1 2" / "1 2 3" …), all isFinal=true —
      // confirmed from on-device trace. Naively joining those snapshots produced
      // a growing-prefix staircase. Merge with overlap instead: cumulative
      // snapshots collapse back to the latest full phrase, while a normal
      // browser's independent segments (no overlap) still concatenate. Merging
      // is also idempotent — re-emitted identical finals fold to the same string.
      let sessionFinal = '';
      const interimParts: string[] = [];
      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        const text = (result[0]?.transcript ?? '').trim();
        if (!text) continue;
        if (result.isFinal) {
          sessionFinal = mergeWithOverlap(sessionFinal, text);
        } else {
          interimParts.push(text);
        }
      }
      sessionFinalRef.current = sessionFinal;

      const committed = committedTranscriptRef.current;
      const nextTranscript =
        committed && sessionFinal
          ? committed + ' ' + sessionFinal
          : committed || sessionFinal;
      setTranscript(nextTranscript);
      setInterimText(interimParts.join(' '));
    };

    rec.onerror = (event: any) => {
      if (event?.error === 'not-allowed') {
        setMicError('Microphone access denied — enable it in browser settings');
        wantRecordingRef.current = false;
        setIsRecording(false);
        setInterimText('');
      }
    };

    rec.onend = () => {
      setInterimText('');
      // Browser auto-stops after ~30–60s on Chrome. Restart if we still want recording.
      if (wantRecordingRef.current) {
        // Fold this session's final text into the committed base BEFORE
        // restarting. The next session's event.results resets to index 0, so
        // without this fold the recomputed transcript would lose prior text.
        const sessionFinal = sessionFinalRef.current;
        if (sessionFinal) {
          // Overlap-aware fold: on mobile the restarted session re-recognizes
          // the same speech, so strip the longest word overlap to avoid the
          // growing-prefix staircase. On desktop there is no overlap and this
          // behaves like a plain concatenation.
          committedTranscriptRef.current = mergeWithOverlap(
            committedTranscriptRef.current,
            sessionFinal,
          );
        }
        sessionFinalRef.current = '';
        try {
          rec.start();
          return;
        } catch {
          // fall through to stopped state
        }
      }
      setIsRecording(false);
    };

    // Fresh start (user pressed record): seed the committed base from whatever
    // is already in the transcript (typed text, or a prior recording the user
    // resumed) and reset the per-session final. Restart-via-onend must NOT run
    // this — it only grows committedTranscriptRef.
    committedTranscriptRef.current = transcript;
    sessionFinalRef.current = '';
    wantRecordingRef.current = true;
    setMicError(null);
    try {
      rec.start();
      recognitionRef.current = rec;
      setIsRecording(true);
    } catch {
      wantRecordingRef.current = false;
      setIsRecording(false);
    }
  }, [transcript]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      stopRecognition();
    };
  }, [stopRecognition]);

  // Auto-start dictation when the panel becomes expanded (including restore from
  // mini), so the user doesn't have to press "Dictate" after opening. The
  // !isRecording guard prevents double-starting; !micError stops a retry loop
  // after a permission denial (onerror sets isRecording=false). startRecognition
  // is idempotent, so a StrictMode double-invoke won't stack instances. The
  // existing Dictate/Stop toggle still lets the user stop and restart manually.
  useEffect(() => {
    if (widgetState === 'expanded' && speechSupported && !isRecording && !micError) {
      startRecognition();
    }
  }, [widgetState, speechSupported, isRecording, micError, startRecognition]);

  const handleOpen = () => {
    setTranscript('');
    setElapsedSeconds(0);
    setSaveState('idle');
    setInterimText('');
    setMicError(null);
    setWidgetState('expanded');
  };

  const handleMinimize = () => {
    setWidgetState('mini');
  };

  const handleExpand = () => {
    setWidgetState('expanded');
  };

  const closeAndReset = useCallback(() => {
    stopRecognition();
    setWidgetState('closed');
    setTranscript('');
    setElapsedSeconds(0);
    setSaveState('idle');
    setInterimText('');
    setMicError(null);
  }, [stopRecognition]);

  const handleCancel = () => {
    closeAndReset();
  };

  const handleSave = async () => {
    if (!transcript.trim() || saveState === 'saving') return;
    // Stop dictation before saving so audio capture ends immediately.
    stopRecognition();
    setSaveState('saving');
    try {
      await createVoiceFeedback.mutateAsync({ transcript: transcript.trim() });
      setSaveState('saved');
      setTimeout(() => {
        closeAndReset();
      }, 2000);
    } catch {
      setSaveState('idle');
    }
  };

  const handleToggleMic = () => {
    if (!speechSupported) return;
    if (isRecording) {
      stopRecognition();
    } else {
      startRecognition();
    }
  };

  if (isLoading || !isAdmin || !showOnDev) {
    return null;
  }

  // ----- Closed state: FAB -----
  if (widgetState === 'closed') {
    return (
      <button
        type="button"
        onClick={handleOpen}
        aria-label="Open voice feedback"
        title="Voice feedback (dev only)"
        className="fixed bottom-4 right-4 z-40 w-14 h-14 rounded-full bg-primary-600 hover:bg-primary-700 text-white shadow-lg flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900"
      >
        <Mic className="w-6 h-6" aria-hidden="true" />
      </button>
    );
  }

  // ----- Mini state: compact horizontal bar -----
  if (widgetState === 'mini') {
    const lastLine = getLastNonEmptyLine(interimText || transcript) || 'Listening…';
    return (
      <div className="fixed bottom-4 right-4 z-40 pointer-events-none">
        <div
          role="button"
          tabIndex={0}
          aria-label="Expand voice feedback"
          onClick={handleExpand}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleExpand();
            }
          }}
          className="pointer-events-auto w-96 sm:w-[28rem] flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors"
        >
          <Mic
            className={`w-4 h-4 flex-shrink-0 ${
              isRecording
                ? 'text-red-600 dark:text-red-400 animate-pulse'
                : 'text-primary-600 dark:text-primary-400'
            }`}
            aria-hidden="true"
          />
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex-shrink-0">
            Voice feedback
          </span>
          <span
            className={`text-xs font-mono px-1.5 py-0.5 rounded flex-shrink-0 ${
              elapsedSeconds <= TIMER_LIMIT_SECONDS
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                : 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200'
            }`}
            aria-live="off"
          >
            {formatTimer(elapsedSeconds)}
          </span>
          <span
            className="flex-1 min-w-0 text-xs text-gray-600 dark:text-gray-300 truncate"
            title={lastLine}
          >
            {lastLine}
          </span>
          <IconButton
            aria-label="Expand voice feedback"
            onClick={(e) => {
              e.stopPropagation();
              handleExpand();
            }}
            className="min-h-[32px] min-w-[32px] flex-shrink-0"
          >
            <Maximize2 className="w-4 h-4" aria-hidden="true" />
          </IconButton>
        </div>
      </div>
    );
  }

  // ----- Expanded state: full panel -----
  const isSaving = saveState === 'saving';
  const showSaved = saveState === 'saved';

  return (
    <div
      role="dialog"
      aria-label="Voice feedback"
      className="fixed bottom-4 right-4 z-40 w-80 sm:w-96 max-h-[80vh] flex flex-col bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl"
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 min-w-0">
          <Mic
            className={`w-4 h-4 flex-shrink-0 ${
              isRecording
                ? 'text-red-600 dark:text-red-400 animate-pulse'
                : 'text-primary-600 dark:text-primary-400'
            }`}
            aria-hidden="true"
          />
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
            Voice feedback
          </span>
          <span
            className={`text-xs font-mono px-1.5 py-0.5 rounded ${
              elapsedSeconds <= TIMER_LIMIT_SECONDS
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                : 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200'
            }`}
            aria-live="off"
          >
            {formatTimer(elapsedSeconds)}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <IconButton
            aria-label="Minimize voice feedback"
            onClick={handleMinimize}
            className="min-h-[32px] min-w-[32px]"
          >
            <Minimize2 className="w-4 h-4" aria-hidden="true" />
          </IconButton>
          <IconButton
            aria-label="Close voice feedback"
            onClick={handleCancel}
            className="min-h-[32px] min-w-[32px]"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </IconButton>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-3">
        <div className="flex items-center justify-between mb-2">
          <label
            htmlFor="voice-feedback-transcript"
            className="text-xs font-medium text-gray-700 dark:text-gray-300"
          >
            Transcript
          </label>
          <button
            type="button"
            onClick={handleToggleMic}
            disabled={!speechSupported || isSaving}
            aria-label={isRecording ? 'Stop dictation' : 'Start dictation'}
            aria-pressed={isRecording}
            title={
              !speechSupported
                ? 'Browser dictation not supported — use OS dictation'
                : isRecording
                  ? 'Stop dictation'
                  : 'Start dictation'
            }
            className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed ${
              isRecording
                ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-900/50'
                : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            {isRecording ? (
              <>
                <MicOff className="w-3.5 h-3.5" aria-hidden="true" />
                Stop
              </>
            ) : (
              <>
                <Mic className="w-3.5 h-3.5" aria-hidden="true" />
                Dictate
              </>
            )}
          </button>
        </div>
        <textarea
          id="voice-feedback-transcript"
          ref={textareaRef}
          value={transcript}
          onChange={(e) => setTranscript(e.target.value)}
          rows={16}
          placeholder="Click Dictate above, or use your OS dictation (⌘ Fn Fn on Mac, Win+H on Windows), and ramble — anything goes."
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          style={{ resize: 'none' }}
          disabled={isSaving}
        />
        {isRecording && interimText && (
          <p
            className="mt-1 text-xs italic text-gray-500 dark:text-gray-400"
            aria-live="polite"
          >
            {interimText}
          </p>
        )}
        {micError && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
            {micError}
          </p>
        )}
        {showSaved && (
          <p className="mt-2 text-xs text-green-700 dark:text-green-400 inline-flex items-center gap-1">
            <Check className="w-3.5 h-3.5" aria-hidden="true" />
            Saved — closing
          </p>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-gray-200 dark:border-gray-700">
        <Button variant="secondary" size="sm" onClick={handleCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={!transcript.trim() || isSaving}
          leadingIcon={isSaving ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : undefined}
        >
          {isSaving ? 'Saving' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
